import { useState } from 'react';
import {
  DEFAULT_SUBSCRIPTION_CATEGORIES,
  SUBSCRIPTION_FREQUENCIES,
  SUBSCRIPTION_FREQUENCY_LABELS,
  type Subscription,
  type SubscriptionFrequency,
  type SubscriptionInput,
} from '../../lib/subscriptionsApi';
import { Select } from '../ui/Select';
import { useCompanies } from '../../hooks/useCompanies';
import { useExpenseCategories } from '../../hooks/useExpenseCategories';
import { useSubscriptionCategories } from '../../hooks/useSubscriptions';
import { useSuppliers } from '../../hooks/useSuppliers';
import { useActiveCompanyId } from '../../store/useCompanyStore';

interface SubscriptionFormModalProps {
  title: string;
  initial?: Subscription;
  prefill?: Partial<SubscriptionInput>;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (input: SubscriptionInput) => void | Promise<void>;
}

export function SubscriptionFormModal({
  title,
  initial,
  prefill,
  submitting,
  onClose,
  onSubmit,
}: SubscriptionFormModalProps) {
  const [name, setName] = useState(initial?.name ?? prefill?.name ?? '');
  const [patternsText, setPatternsText] = useState(
    (initial?.merchantPatterns ?? prefill?.merchantPatterns ?? []).join(', '),
  );
  const [category, setCategory] = useState<string>(
    initial?.category ?? prefill?.category ?? 'other',
  );
  // Settings-managed nomenclature; seed defaults as fallback while loading.
  const { data: categoryDefs } = useSubscriptionCategories();
  const categories =
    categoryDefs && categoryDefs.length > 0 ? categoryDefs : DEFAULT_SUBSCRIPTION_CATEGORIES;
  const [expectedAmount, setExpectedAmount] = useState(
    initial?.expectedAmount != null ? String(initial.expectedAmount) : '',
  );
  const [expectedCurrency, setExpectedCurrency] = useState(
    initial?.expectedCurrency ?? prefill?.expectedCurrency ?? 'RON',
  );
  const [frequency, setFrequency] = useState<SubscriptionFrequency>(
    initial?.frequency ?? (prefill?.frequency as SubscriptionFrequency | undefined) ?? 'monthly',
  );
  // Day of month the subscription is charged (cash-flow projection); only
  // meaningful for monthly subscriptions.
  const [paymentDay, setPaymentDay] = useState(
    initial?.paymentDay != null ? String(initial.paymentDay) : '',
  );
  const [notes, setNotes] = useState(initial?.notes ?? prefill?.notes ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const { data: companies } = useCompanies();
  const multiFirm = (companies?.length ?? 0) > 1;
  const activeCompanyId = useActiveCompanyId();
  const [companyId, setCompanyId] = useState<string>(
    initial ? (initial.companyId ?? '') : (activeCompanyId ?? ''),
  );

  // Optional supplier link (bank-transfer subscriptions, e.g. hosting billed
  // by an SRL). Active suppliers, alphabetical (API limit: first 100).
  const { data: suppliersData } = useSuppliers({ active: 'true', sort: 'name', limit: 100 });
  const suppliers = suppliersData?.data ?? [];
  const [supplierId, setSupplierId] = useState<string>(initial?.supplierId ?? '');

  // Default expense category preselected on bank-statement reconciliation
  // ('' = none). The generic income/expense defaults are excluded — they are
  // the fallback categories every line already gets at import.
  // Categories this firm sees (shared + its own); '' = the default firm.
  const { data: expenseCategories } = useExpenseCategories(
    false,
    companyId || companies?.find((c) => c.isDefault)?.id,
  );
  const expenseCategoryOptions = (expenseCategories ?? []).filter(
    (c) => c.code !== 'income' && c.code !== 'expense',
  );
  const [expenseCategoryId, setExpenseCategoryId] = useState<string>(
    initial?.expenseCategoryId ?? '',
  );

  const isCreate = !initial;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Numele este obligatoriu.');
      return;
    }
    const patterns = patternsText
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);

    const input: SubscriptionInput = {
      name: name.trim(),
      merchantPatterns: patterns,
      category,
      expectedAmount: expectedAmount ? parseFloat(expectedAmount) : null,
      expectedCurrency: expectedCurrency || 'RON',
      frequency,
      paymentDay: frequency === 'monthly' && paymentDay !== '' ? parseInt(paymentDay, 10) : null,
      notes: notes.trim() || undefined,
      isActive,
      supplierId: supplierId === '' ? null : supplierId,
      expenseCategoryId: expenseCategoryId === '' ? null : expenseCategoryId,
    };
    if (multiFirm) {
      if (initial) input.companyId = companyId === '' ? null : companyId;
      else if (companyId) input.companyId = companyId;
    }
    try {
      await onSubmit(input);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'A apărut o eroare.');
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Închide"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-600">Nume *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Patronaje POS (separate prin virgulă, * = orice caractere)
              </label>
              <input
                type="text"
                value={patternsText}
                onChange={(e) => setPatternsText(e.target.value)}
                placeholder="ex. ANTHROPIC*, SLACK*, PayU*vodafone*"
                className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Folosește * ca wildcard. Exemplu: ANTHROPIC* potrivește „ANTHROPIC* CLAUDE SUB".
              </p>
            </div>

            <div>
              <span className="mb-1 block text-xs font-medium text-gray-600">Categorie</span>
              <Select
                label="Categorie"
                hideLabel
                value={category}
                onChange={(v) => setCategory(v)}
                options={categories.map((c) => ({ value: c.code, label: c.label }))}
              />
            </div>

            <div>
              <span className="mb-1 block text-xs font-medium text-gray-600">Frecvență</span>
              <Select
                label="Frecvență"
                hideLabel
                value={frequency}
                onChange={(v) => setFrequency(v as SubscriptionFrequency)}
                options={SUBSCRIPTION_FREQUENCIES.map((f) => ({
                  value: f,
                  label: SUBSCRIPTION_FREQUENCY_LABELS[f],
                }))}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Ziua plății (1–31)
              </label>
              <input
                type="number"
                min="1"
                max="31"
                step="1"
                value={paymentDay}
                onChange={(e) => setPaymentDay(e.target.value)}
                disabled={frequency !== 'monthly'}
                placeholder="1"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-gray-50 disabled:text-gray-400"
              />
              <p className="mt-1 text-xs text-gray-500">
                Doar pentru abonamente lunare; folosită în proiecția de cash flow.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Sumă estimată</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={expectedAmount}
                onChange={(e) => setExpectedAmount(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Monedă</label>
              <input
                type="text"
                value={expectedCurrency ?? ''}
                onChange={(e) => setExpectedCurrency(e.target.value)}
                placeholder="RON"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <span className="mb-1 block text-xs font-medium text-gray-600">Furnizor</span>
              <Select
                label="Furnizor"
                hideLabel
                clearable
                value={supplierId}
                onChange={setSupplierId}
                placeholder="Fără furnizor"
                options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
              />
              <p className="mt-1 text-xs text-gray-500">
                Leagă abonamentul de furnizorul care îl facturează (plăți prin transfer bancar).
              </p>
            </div>

            <div>
              <span className="mb-1 block text-xs font-medium text-gray-600">
                Categorie cheltuială implicită
              </span>
              <Select
                label="Categorie cheltuială implicită"
                hideLabel
                clearable
                value={expenseCategoryId}
                onChange={setExpenseCategoryId}
                placeholder="Fără categorie implicită"
                options={expenseCategoryOptions.map((c) => ({ value: c.id, label: c.name }))}
              />
              <p className="mt-1 text-xs text-gray-500">
                Preselectată automat la reconcilierea extraselor. Are prioritate față de categoria
                implicită a furnizorului legat.
              </p>
            </div>

            {multiFirm && companies && (
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-600">Firmă</span>
                <Select
                  label="Firmă"
                  hideLabel
                  clearable
                  value={companyId}
                  onChange={setCompanyId}
                  placeholder="Firma implicită"
                  options={companies.map((c) => ({
                    value: c.id,
                    label: c.displayName || c.legalName,
                  }))}
                />
              </div>
            )}

            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-600">Notițe</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            {!isCreate && (
              <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                Abonament activ
              </label>
            )}
          </div>

          {error && <p className="px-5 pb-2 text-sm text-red-600">{error}</p>}

          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Anulează
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              Salvează
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
